// --------------------------------------------------------------
// Created On: 2024-05-14
// Author: KC Willard
//
// Last Modified: 2026-01-12
// Modified By: Marco Serrano
//
// Copyright 2025 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import styles from "./ConfirmOrderForm.module.scss";
import {
  SHOW_SALES_ORDERS_NOT_PLACED_DISCLAIMER,
  SALES_ORDER_NOT_GOING_TO_BE_PLACED_DISCLAIMER,
} from "../../../constants/miscellaneous";
import SaveQuoteModal from "../../../components/SaveQuoteModal/SaveQuoteModal";
import { Link } from "react-router-dom";

// Form for users to read over the terms and conditions, ensure they consent and then submit the order to be posted to Epicor.
export default function ConfirmOrderForm(props: Props): Component {
  const [showModal, setShowModal] = useState<boolean>(false);
  function handleConfirmation() {
    if (props.orderIsValid && props.consentChecked) {
      // Save a new quote.
      if (props.isQuote && props.quoteId === undefined) {
        props.onSaveQuote();
      }
      // Submit a new order.
      else if (!props.isQuote && props.quoteId === undefined) {
        props.onSubmitOrder();
      }
      // Submit an order from a quote.
      else if (props.isQuote && props.quoteId !== undefined) {
        props.onSubmitOrder();
      } else {
        props.onErrorMessageChange("Internal server error. Order details could not be saved.");
      }
    }
  }

  return (
    <div className="py-2">
      <div>
        <p>
          This form collects your information so that we can contact you about products, services, and other information
          you may have requested from us. Please read our full Privacy Policy and Terms of Use for details about how we
          protect and manage your data.
        </p>
      </div>
      <div className={`${styles.checkboxRow} row align-items-center p-3`}>
        <div className="col-3"></div>
        <div className={`col-auto form-check`}>
          <input
            className="form-check-big form-check-input"
            data-test="consent-checkbox-input"
            type="checkbox"
            checked={props.consentChecked}
            onChange={() => props.onChangeConsentChecked(!props.consentChecked)}
            id="consentCheckbox"
          />
        </div>
        <div className="col">
          <label className={`${styles.consentLabel} form-check-label ps-2`} htmlFor="consentCheckbox">
            I consent to have Cornell Pump Company contact me using the information I have provided, and I have read and
            understand the <Link to="/privacy-policy-text">Privacy Policy</Link>,{" "}
            <Link to="/terms-and-conditions-of-sale-text">Terms and Conditions of Sale</Link>, and Terms of Use
            provided.
          </label>
        </div>
        <div className="col-3"></div>
      </div>
      {props.quoteId === undefined && (
        <div className={`${styles.checkboxRow} row align-items-center p-3`}>
          <div className="col-3"></div>
          <div className={`col-auto form-check`}>
            <input
              className="form-check-big form-check-input"
              data-test="invoice-only-checkbox-input"
              type="checkbox"
              checked={props.isQuote}
              onChange={() => props.onChangeInvoiceOnlyChecked(!props.isQuote)}
              id="invoiceOnlyCheckbox"
            />
          </div>
          <div className="col">
            <label className={`${styles.consentLabel} form-check-label ps-2`} htmlFor="invoiceOnlyCheckbox">
              Save as quote.<br></br> <strong>Quoted prices are valid for 30 days from quote creation.</strong>
            </label>
          </div>
          <div className="col-3"></div>
        </div>
      )}
      <div>
        <p>
          Orders placed online will be processed by Cornell Employees upon payment and are subject to the standard Terms
          and Conditions of Sale. Please refer to our Return Policy before purchase. For further questions regarding
          your account or this order, contact Cornell during regular business hours (7am - 5pm PST) at +1 (503) 653-0330
        </p>
      </div>
      {SHOW_SALES_ORDERS_NOT_PLACED_DISCLAIMER && (
        <p className={styles.salesOrderDisclaimer}>{SALES_ORDER_NOT_GOING_TO_BE_PLACED_DISCLAIMER}</p>
      )}
      <div className={`${styles.buttonRow} row`}>
        <button
          data-test="checkout-button"
          type="button"
          className="btn btn-primary col-6 col-lg-3"
          data-dismiss="modal"
          onClick={() => {
            if (props.isQuote && props.quoteId === undefined) {
              setShowModal(true);
            } else {
              handleConfirmation();
            }
          }}
          disabled={!props.orderIsValid || !props.consentChecked}
        >
          {!props.isQuote || props.quoteId !== undefined ? "Submit Order" : "Save Quote"}
        </button>
      </div>
      <SaveQuoteModal
        showModal={showModal}
        quoteName={props.quoteName}
        shouldClearCart={props.shouldClearCart}
        onChangeQuoteName={props.onChangeQuoteName}
        onChangeShouldClearCart={props.onChangeShouldClearCart}
        onClose={() => setShowModal(false)}
        onSave={props.onSaveQuote}
      />
    </div>
  );
}

interface Props {
  consentChecked: boolean;
  orderIsValid: boolean;
  isQuote: boolean;
  quoteId: string | undefined;
  quoteName: string;
  shouldClearCart: boolean;
  onChangeConsentChecked: (consentChecked: boolean) => void;
  onSubmitOrder: () => void;
  onSaveQuote: () => void;
  onChangeInvoiceOnlyChecked: (invoiceOnlyChecked: boolean) => void;
  onErrorMessageChange: (message: string) => void;
  onChangeQuoteName: (quoteName: string) => void;
  onChangeShouldClearCart: (shouldClearCart: boolean) => void;
}
